import type { Location, Store } from "@hobbyshopify/backend-client";
import type { Workspace } from "../repositories/store-repository";

/** One line of the picker: a shop at one of its counters, or a shop with none yet. */
export interface WorkspaceOption {
  readonly store: Store;
  readonly location: Location | null;
}

/** A booth opens the day before its show, for setting up, and closes the day after. */
const BOOTH_MARGIN_MS = 24 * 60 * 60 * 1000;

const boothOpen = (location: Location, now: Date) =>
  !location.event ||
  (now.getTime() >= location.event.startsAt.getTime() - BOOTH_MARGIN_MS &&
    now.getTime() <= location.event.endsAt.getTime() + BOOTH_MARGIN_MS);

/** The counters a merchant can work at: every active location, but a booth only around its show. */
export const activeLocations = (locations: readonly Location[], now: Date = new Date()) =>
  locations.filter((l) => l.isActive && (l.kind !== "BOOTH" || boothOpen(l, now)));

export const workspaceKey = (w: Workspace) => `${w.storeId}:${w.locationId ?? ""}`;

/** Every shop × active location, by shop name then location name; a shop with no location is one line of its own. */
export function workspaceOptions(
  entries: readonly { store: Store; locations: readonly Location[] }[],
  now: Date = new Date(),
): WorkspaceOption[] {
  const byName = (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? "");
  return [...entries]
    .sort((a, b) => byName(a.store.name, b.store.name))
    .flatMap<WorkspaceOption>(({ store, locations }) => {
      const active = activeLocations(locations, now).sort((a, b) => byName(a.name, b.name));
      return active.length === 0 ? [{ store, location: null }] : active.map((location) => ({ store, location }));
    });
}

/** Whether the chosen workspace still names something the selected shop has. */
export function isWorkspaceValid(
  workspace: Workspace | null,
  locations: readonly Location[],
  now: Date = new Date(),
): boolean {
  if (!workspace) return false;
  const active = activeLocations(locations, now);
  if (workspace.locationId === null) return active.length === 0;
  return active.some((l) => l.id === workspace.locationId);
}

/**
 * The workspace to take without asking, or null when the merchant has to pick:
 * one shop with at most one counter is the only answer there is.
 */
export function implicitWorkspace(
  stores: readonly Store[],
  store: Store,
  locations: readonly Location[],
  now: Date = new Date(),
): Workspace | null {
  if (stores.length > 1) return null;
  const active = activeLocations(locations, now);
  if (active.length > 1) return null;
  return { storeId: store.id, locationId: active[0]?.id ?? null };
}

/** The id a shop route or a customer link takes: `loc_<id>` names one counter, a bare store id the shop's primary one. */
export const shopEntryId = (storeId: string, locationId: string | null) => (locationId ? `loc_${locationId}` : storeId);

/** A label for the header: "Shop · Counter". */
export function workspaceLabel(store: Store | null, location: Location | null): string {
  const shop = store?.name?.trim() || "Your shop";
  return location ? `${shop} · ${location.name}` : shop;
}
